import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Mic, Loader2, Bell, Camera, Youtube, Scissors, FileText, Quote, Play, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Footer } from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_HERO = "https://static.prod-images.emergentagent.com/jobs/adb5decd-5900-4816-abd1-ea444c906724/images/3e3db35e2a6f322550cee2319949f2ac240218d00df08d840c5fa25f473336c8.jpeg";

function mediaUrl(src) {
  if (!src) return "";
  return src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src;
}

function embedFor(url) {
  if (!url) return null;
  let m;
  if ((m = url.match(/open\.spotify\.com\/(episode|show|album|track|playlist)\/([A-Za-z0-9]+)/)))
    return { src: `https://open.spotify.com/embed/${m[1]}/${m[2]}?theme=0`, height: 232, platform: "Spotify" };
  if ((m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/)))
    return { src: `https://www.youtube.com/embed/${m[1]}?autoplay=1`, height: 420, platform: "YouTube" };
  if (/podcasts\.apple\.com/.test(url))
    return { src: url.replace("podcasts.apple.com", "embed.podcasts.apple.com"), height: 175, platform: "Apple Podcasts" };
  return null;
}

const TopBar = () => (
  <header className="fixed top-0 left-0 right-0 z-[1000] bg-black/70 backdrop-blur-xl border-b border-white/10">
    <div className="flex items-center justify-between px-5 md:px-10 h-[68px]">
      <Link to="/" className="font-display text-2xl md:text-3xl tracking-tight leading-none"><span className="text-[#E30613]">VL</span><span className="text-white">MAG</span></Link>
      <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors"><ArrowLeft size={16} /> Back To VLMAG</Link>
    </div>
  </header>
);

function NotifyForm({ sfx = "" }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [state, setState] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { setState("error:Add your email to get notified."); return; }
    setState("sending");
    try {
      const { data } = await axios.post(`${API}/podcast/notify`, form);
      setState(data.already ? "already" : "done");
    } catch { setState("error:Couldn't sign you up. Try again."); }
  };
  const cls = "h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600";

  if (state === "done" || state === "already") return (
    <div className="border border-[#E30613] bg-[#E30613]/10 p-6" data-testid={`podcast-notify-done${sfx}`}>
      <p className="font-display uppercase text-2xl text-white">{state === "already" ? "You're already on the list." : "You're on the list."}</p>
      <p className="text-neutral-400 text-sm mt-2">We'll let you know the moment <span className="text-white">BEYOND THE VISUAL</span> drops its first conversation.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="max-w-lg" data-testid={`podcast-notify-form${sfx}`}>
      <div className="flex flex-col sm:flex-row gap-3">
        <Input data-testid={`podcast-notify-name${sfx}`} value={form.name} onChange={set("name")} placeholder="Name (optional)" className={cls} />
        <Input data-testid={`podcast-notify-email${sfx}`} type="email" value={form.email} onChange={set("email")} placeholder="Your email" className={cls} />
      </div>
      {state.startsWith("error") && <p className="text-[#E30613] text-sm mt-3" data-testid={`podcast-notify-error${sfx}`}>{state.slice(6)}</p>}
      <button type="submit" disabled={state === "sending"} data-testid={`podcast-notify-submit${sfx}`} className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60">
        {state === "sending" ? <Loader2 className="animate-spin" size={14} /> : <Bell size={14} />} Notify Me When It Drops
      </button>
    </form>
  );
}

function EpisodeCard({ ep, onPlay }) {
  const emb = embedFor(ep.embed_url);
  return (
    <motion.article data-testid={`podcast-episode-${ep.id}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.6 }} className="group border border-white/10 hover:border-[#E30613] transition-colors duration-500">
      <button onClick={() => emb && onPlay(ep)} className="relative aspect-video w-full overflow-hidden block bg-neutral-900" disabled={!emb}>
        {ep.cover ? <img src={mediaUrl(ep.cover)} alt={ep.title} className="w-full h-full object-cover grayscale-img group-hover:scale-105 transition-transform duration-700" /> : <div className="w-full h-full flex items-center justify-center"><Mic size={28} className="text-neutral-700" /></div>}
        {emb && <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity"><span className="w-14 h-14 rounded-full bg-[#E30613] flex items-center justify-center"><Play size={20} fill="#fff" className="ml-0.5" /></span></span>}
      </button>
      <div className="p-5">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#E30613]">S{ep.season_number}{ep.episode_number ? ` · EP ${ep.episode_number}` : ""}{ep.duration ? ` · ${ep.duration}` : ""}</p>
        <h3 className="font-display uppercase text-xl leading-tight tracking-tight text-white mt-2 group-hover:text-[#E30613] transition-colors">{ep.title}</h3>
        {ep.guest && <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-neutral-500 mt-2">{ep.guest}{ep.guest_role ? ` · ${ep.guest_role}` : ""}</p>}
        {ep.description && <p className="text-sm text-neutral-400 mt-3 leading-relaxed line-clamp-3">{ep.description}</p>}
      </div>
    </motion.article>
  );
}

const FORMATS = [
  { icon: Mic, label: "The Podcast", note: "Full audio conversation" },
  { icon: Youtube, label: "YouTube Episode", note: "The visual cut" },
  { icon: Scissors, label: "Social Clips", note: "Moments made to travel" },
  { icon: Camera, label: "Editorial Portraits", note: "Photography from the set" },
  { icon: FileText, label: "VLMAG Article", note: "The story, written" },
  { icon: Quote, label: "Quotes & BTS", note: "Behind the scenes" },
];

const CONCEPT = [
  "We see the clothing. We hear the song. We see the photograph. We visit the business. We experience the artwork.",
  "But we don't always know what it took to create it.",
  "BEYOND THE VISUAL explores that part of the story — the person, the mindset, the journey behind the work.",
];

export default function PodcastPage() {
  const [settings, setSettings] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [player, setPlayer] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/podcast`).then((r) => { setSettings(r.data.settings); setEpisodes(r.data.episodes || []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const hero = settings?.hero_image || FALLBACK_HERO;
  const live = settings?.live && episodes.length > 0;
  const seasonTitle = settings?.season_title || "CULTURED";
  const seasonNum = settings?.season_number || "01";
  const tagline = settings?.tagline || "Creatives Are The Superstars.";
  const featured = episodes.find((e) => e.featured) || episodes[0];
  const rest = live ? episodes.filter((e) => !featured || e.id !== featured.id) : [];
  const playerEmbed = player ? embedFor(player.embed_url) : null;

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-[#E30613]" size={30} /></div>;

  return (
    <div className="bg-black text-white min-h-screen" data-testid="podcast-page">
      <TopBar />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={hero} alt="" className="w-full h-full object-cover grayscale-img opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
        </div>
        <div className="relative px-5 md:px-10 pb-16 md:pb-24 pt-[120px] w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
            <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold text-[#E30613] mb-5" data-testid="podcast-hero-badge">
              <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E30613] opacity-75" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#E30613]" /></span>
              The VLMAG Podcast · {live ? `Season ${seasonNum} — ${seasonTitle}` : "Coming Soon"}
            </p>
            <h1 className="font-display uppercase leading-[0.82] tracking-tight text-5xl sm:text-7xl md:text-[9rem]">Beyond<br />The Visual<span className="text-[#E30613]">™</span></h1>
            <p className="text-lg md:text-xl text-neutral-300 mt-6 max-w-2xl">A VLMAG Original Series. We document what the culture creates — this is where we go beyond it, to the people behind the work.</p>
            <p className="font-display uppercase text-xl md:text-2xl text-white mt-6 tracking-tight">{tagline}</p>
            {!live && <div className="mt-9"><NotifyForm /></div>}
            {live && (
              <a href="#episodes" className="mt-9 inline-flex items-center gap-3 px-7 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors"><Play size={14} fill="#fff" /> Watch The Latest Episode</a>
            )}
          </motion.div>
        </div>
      </section>

      {/* LIVE EPISODES */}
      {live && (
        <section id="episodes" className="px-5 md:px-10 py-14 md:py-20 border-t border-white/10" data-testid="podcast-episodes">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-2">Season {seasonNum} — {seasonTitle}</p>
          <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-none mb-10">Episodes</h2>

          {featured && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 items-center mb-12" data-testid="podcast-featured">
              <div className="lg:col-span-7">
                <button onClick={() => embedFor(featured.embed_url) && setPlayer(featured)} className="relative aspect-video w-full overflow-hidden border border-white/10 block bg-neutral-900 group" disabled={!embedFor(featured.embed_url)}>
                  {featured.cover ? <img src={mediaUrl(featured.cover)} alt={featured.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Mic size={32} className="text-neutral-700" /></div>}
                  {embedFor(featured.embed_url) && <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors"><span className="w-16 h-16 rounded-full bg-[#E30613] flex items-center justify-center"><Play size={24} fill="#fff" className="ml-1" /></span></span>}
                </button>
              </div>
              <div className="lg:col-span-5">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#E30613]">Latest · S{featured.season_number}{featured.episode_number ? ` · EP ${featured.episode_number}` : ""}</p>
                <h3 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-[0.9] mt-3">{featured.title}</h3>
                {featured.guest && <p className="text-neutral-300 mt-4 text-lg">{featured.guest}{featured.guest_role ? ` · ${featured.guest_role}` : ""}</p>}
                {featured.description && <p className="text-neutral-400 mt-4 leading-relaxed">{featured.description}</p>}
              </div>
            </div>
          )}

          {rest.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="podcast-episode-grid">
              {rest.map((ep) => <EpisodeCard key={ep.id} ep={ep} onPlay={setPlayer} />)}
            </div>
          )}
        </section>
      )}

      {/* SIGNATURE QUESTION */}
      <section className="px-5 md:px-10 py-16 md:py-24 border-t border-white/10 text-center" data-testid="podcast-signature">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#E30613] mb-6">The Signature Question</p>
        <h2 className="font-display uppercase text-3xl sm:text-5xl md:text-6xl tracking-tight leading-[0.95] max-w-4xl mx-auto">
          "What do people see —<br /><span className="text-neutral-500">and what don't they see?"</span>
        </h2>
      </section>

      {/* CONCEPT */}
      <section className="px-5 md:px-10 py-14 md:py-20 border-t border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-4">The Idea</p>
            <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-[0.9]">What Is Beyond The Visual?</h2>
          </div>
          <div className="lg:col-span-8 space-y-6">
            {CONCEPT.map((p, i) => (
              <motion.p key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.05 }} className={`leading-relaxed ${i === 1 ? "text-white font-display uppercase text-2xl md:text-3xl tracking-tight" : "text-neutral-300 text-lg"}`}>{p}</motion.p>
            ))}
            <div className="flex flex-wrap gap-x-8 gap-y-3 pt-4 text-sm uppercase tracking-[0.15em] font-bold text-neutral-500">
              <span>What inspired you?</span><span>What did you overcome?</span><span>What are you building next?</span><span>What are you contributing to culture?</span>
            </div>
          </div>
        </div>
      </section>

      {/* SEASON 01 */}
      <section className="px-5 md:px-10 py-14 md:py-20 border-t border-white/10" data-testid="podcast-season">
        <div className="border border-white/10 p-8 md:p-14 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 font-display uppercase text-[10rem] md:text-[16rem] leading-none text-white/[0.03] select-none pointer-events-none">{seasonNum}</div>
          <div className="relative">
            <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#E30613] mb-4">Season {seasonNum}</p>
            <h2 className="font-display uppercase text-5xl sm:text-6xl md:text-8xl tracking-tight leading-[0.85]">{seasonTitle}</h2>
            <p className="text-lg text-neutral-300 mt-6 max-w-2xl">Our first chapter explores the people contributing to today's creative culture — from established names to the talented still building theirs.</p>
            <p className="text-neutral-400 mt-4 max-w-2xl">Fashion. Music. Art. Photography. Film. Design. Business. Because VLMAG was never meant to represent only one kind of creative — a local painter can sit in the same chair as an established musician.</p>
          </div>
        </div>
      </section>

      {/* ONE CONVERSATION → EVERYTHING */}
      <section className="px-5 md:px-10 py-14 md:py-20 border-t border-white/10">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-4">The VLMAG Difference</p>
        <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-[0.9] mb-10 max-w-3xl">One Conversation Becomes An Entire Story.</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="podcast-formats">
          {FORMATS.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div key={f.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 3) * 0.06 }} className="bg-black p-6 md:p-8 group hover:bg-white/[0.03] transition-colors">
                <Icon size={22} className="text-[#E30613] mb-4" />
                <p className="font-display uppercase text-xl md:text-2xl tracking-tight text-white leading-none">{f.label}</p>
                <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-neutral-500 mt-2">{f.note}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* FINAL CTA (coming soon only) */}
      {!live && (
        <section className="px-5 md:px-10 py-16 md:py-28 border-t border-white/10">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-4">Don't Miss Episode One</p>
            <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.9] mb-4">Be First In The Room.</h2>
            <p className="text-neutral-400 mb-8">Season {seasonNum} — {seasonTitle} is in production. Drop your email and you'll be the first to know when BEYOND THE VISUAL goes live.</p>
            <NotifyForm sfx="-2" />
          </div>
        </section>
      )}

      {/* PLAYER MODAL */}
      {player && playerEmbed && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4" data-testid="podcast-player-modal">
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => setPlayer(null)} />
          <div className="relative w-full max-w-4xl">
            <button onClick={() => setPlayer(null)} data-testid="podcast-player-close" className="absolute -top-10 right-0 text-neutral-400 hover:text-white inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold"><X size={18} /> Close</button>
            <div className="bg-black border border-white/15">
              <iframe title={player.title} src={playerEmbed.src} width="100%" height={playerEmbed.height} frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" style={{ borderRadius: 0, display: "block" }} />
            </div>
            <p className="text-white font-bold mt-4">{player.title}</p>
            {player.guest && <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-neutral-500 mt-1">{player.guest}{player.guest_role ? ` · ${player.guest_role}` : ""}</p>}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
